import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProductAnalysisProgressWsMessage } from '@ozon-ai/analysis/contracts';
import { useNavigate, useParams } from 'react-router-dom';
import type { ProductAnalysis, ProductAnalysisRunState } from '@ozon-ai/analysis/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../api/client';
import { ProductAnalysisResultView } from '../../components/product-analysis/ProductAnalysisResultView';
import { ProductAnalysisRunProgressView } from '../../components/product-analysis/ProductAnalysisRunProgressView';
import { isProductAnalysisRunState } from '../../components/product-analysis/productAnalysisRunState';
import { useProductAnalysisProgressWs } from '../../hooks/useProductAnalysisProgressWs';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ContentCard } from '../../ui/ContentCard';
import { ErrorText, PageShell } from '../../ui/PageShell';

type RunResponse = ProductAnalysis | ProductAnalysisRunState;

export function SkuAnalysisRunPage() {
  const { offerId: offerIdParam, runId } = useParams();
  const offerId = offerIdParam ? decodeURIComponent(offerIdParam) : '';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProductAnalysis | null>(null);
  const [runState, setRunState] = useState<ProductAnalysisRunState | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsActiveRef = useRef(false);

  const load = useCallback(async (): Promise<boolean> => {
    if (!runId) return true;
    try {
      const data = await api.get<RunResponse>(`/product-analyses?id=${encodeURIComponent(runId)}`);
      if (data == null) {
        setError('Пустой ответ сервера');
        setResult(null);
        setRunState(null);
        return true;
      }
      if (offerId && isProductAnalysisRunState(data) && data.offerId !== offerId) {
        setError('Анализ не относится к выбранному SKU');
        setResult(null);
        setRunState(null);
        return true;
      }
      if (
        offerId &&
        !isProductAnalysisRunState(data) &&
        data.context?.offerId &&
        data.context.offerId !== offerId
      ) {
        setError('Анализ не относится к выбранному SKU');
        setResult(null);
        setRunState(null);
        return true;
      }

      if (isProductAnalysisRunState(data)) {
        setRunState(data);
        setResult(null);
        setError(data.status === 'failed' ? data.error ?? 'Анализ завершился с ошибкой' : null);
        return data.status !== 'running' && data.status !== 'created';
      }

      setResult(data);
      setRunState(null);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить анализ');
      setResult(null);
      setRunState(null);
      return true;
    } finally {
      setLoading(false);
    }
  }, [offerId, runId]);

  const handleWsMessage = useCallback(
    (message: ProductAnalysisProgressWsMessage) => {
      wsActiveRef.current = true;
      if (message.type === 'progress') {
        setRunState((prev) => {
          if (prev && prev.runId !== message.runId) return prev;
          if (!prev) {
            return {
              kind: 'run',
              runId: message.runId,
              status: message.status,
              offerId,
              periodFrom: '',
              periodTo: '',
              progress: message.progress,
              error: message.error,
            };
          }
          return {
            ...prev,
            status: message.status,
            progress: message.progress,
            error: message.error,
          };
        });
        setLoading(false);
        return;
      }

      if (message.type === 'done') {
        void load();
      }
    },
    [load, offerId]
  );

  const wsEnabled = Boolean(runId) && !result;
  useProductAnalysisProgressWs(runId, wsEnabled, handleWsMessage);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const done = await load();
      if (cancelled || done) {
        return;
      }
      const delay = wsActiveRef.current ? 5000 : 1000;
      pollTimerRef.current = setTimeout(() => {
        void poll();
      }, delay);
    };

    void poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [load]);

  if (!runId || !offerId) {
    return (
      <PageShell title="Результат анализа">
        <ErrorText>Некорректная ссылка</ErrorText>
      </PageShell>
    );
  }

  if (loading && !runState && !result) {
    return <LoadingSpinner />;
  }

  const title = runState?.status === 'running' || runState?.status === 'created'
    ? 'Анализ выполняется'
    : 'Результат анализа';

  return (
    <PageShell
      title={title}
      action={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/analyses/sku/${encodeURIComponent(offerId)}`)}
        >
          Назад
        </Button>
      }
    >
      {error && <ErrorText>{error}</ErrorText>}

      {runState && (runState.status === 'running' || runState.status === 'created') && (
        <ContentCard>
          <ProductAnalysisRunProgressView progress={runState.progress} />
        </ContentCard>
      )}

      {runState?.status === 'failed' && !result && (
        <ContentCard>
          <ProductAnalysisRunProgressView progress={runState.progress} />
        </ContentCard>
      )}

      {result && (
        <ContentCard>
          <ProductAnalysisResultView result={result} />
        </ContentCard>
      )}

      {!error && !result && !runState && !loading && (
        <ContentCard>
          <ErrorText>Нет данных по этому запуску. Обновите страницу или вернитесь к списку.</ErrorText>
        </ContentCard>
      )}
    </PageShell>
  );
}
